import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  ShieldCheck, Warning, Info, ArrowsClockwise, CaretDown, CaretRight,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

/**
 * Finance Validation Widget — surfaces cross-module integrity issues:
 * - Orphan finance entries (no project link)
 * - Finance entries missing category
 * - Invoices/procurement without project link
 * - Duplicate finance entries
 * - Project budget overruns (>5%)
 * - Negative invoices
 *
 * Calls GET /api/finance/validation-flags.
 */
export default function FinanceValidationWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(true);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data: r } = await api.get("/finance/validation-flags");
      setData(r);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (err) {
    return (
      <div className="dc-card p-4 border-amber-200 bg-amber-50/60" data-testid="finance-validation-err">
        <div className="flex items-center gap-2 text-amber-800">
          <Warning size={14} weight="fill" /> Could not load finance validation flags · {err}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="dc-card p-4" data-testid="finance-validation-loading">
        <div className="overline">Finance Validation</div>
        <div className="mt-1 font-mono-alt text-[11px] uppercase tracking-widest text-slate-400">Loading checks…</div>
      </div>
    );
  }

  const issues = data.issues || [];
  const allClean = issues.length === 0;

  const errors = issues.filter((i) => i.severity === "error");
  const warnings = issues.filter((i) => i.severity === "warning");
  const infos = issues.filter((i) => i.severity === "info");

  return (
    <div className={`dc-card overflow-hidden ${allClean ? "border-emerald-200" : ""}`} data-testid="finance-validation-widget">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }}
        className="w-full flex items-center gap-2 border-b border-slate-100 px-5 py-3 text-left hover:bg-slate-50/60 cursor-pointer"
        data-testid="finance-validation-toggle"
      >
        {open ? <CaretDown size={14} className="text-slate-500" /> : <CaretRight size={14} className="text-slate-500" />}
        <ShieldCheck size={16} weight="fill" className={allClean ? "text-emerald-600" : "text-amber-600"} />
        <div className="flex-1">
          <div className="font-display text-[14px] font-semibold text-slate-900">Finance Validation</div>
          <div className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
            Cross-module integrity checks · last run {data.checked_at ? new Date(data.checked_at).toLocaleTimeString("en-GB", { timeZone: "Africa/Mogadishu" }) + " EAT" : "—"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {allClean ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-emerald-700 border border-emerald-200">
              All checks passed
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-amber-800 border border-amber-200">
              {data.issue_count} issue{data.issue_count === 1 ? "" : "s"}
            </span>
          )}
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); load(); }} disabled={loading} data-testid="finance-validation-refresh">
            <ArrowsClockwise size={12} className={loading ? "animate-spin" : ""} />
          </Button>
        </div>
      </div>
      {open && (
        <div className="p-5">
          {allClean ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50/60 px-4 py-3 flex items-center gap-2" data-testid="finance-validation-clean">
              <ShieldCheck size={16} weight="fill" className="text-emerald-600" />
              <div className="text-sm text-emerald-800">All cross-module finance integrity checks passed. Every entry is properly linked and categorised.</div>
            </div>
          ) : (
            <div className="space-y-2">
              {errors.map((i, idx) => <IssueRow key={`err-${idx}`} issue={i} tone="red" />)}
              {warnings.map((i, idx) => <IssueRow key={`warn-${idx}`} issue={i} tone="amber" />)}
              {infos.map((i, idx) => <IssueRow key={`info-${idx}`} issue={i} tone="blue" />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function IssueRow({ issue, tone }) {
  const TONE = {
    red: "border-red-200 bg-red-50/60 text-red-800",
    amber: "border-amber-200 bg-amber-50/60 text-amber-800",
    blue: "border-blue-200 bg-blue-50/60 text-blue-800",
  }[tone] || "border-slate-200 bg-slate-50/60 text-slate-800";
  const Icon = tone === "blue" ? Info : Warning;
  return (
    <div className={`rounded-md border px-3 py-2 ${TONE} flex items-start gap-2`} data-testid={`finance-issue-${issue.rule}`}>
      <Icon size={14} weight="fill" className="mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-widest">{issue.severity} · {issue.rule}</div>
        <div className="text-sm">{issue.message}</div>
      </div>
      {issue.count != null && (
        <span className="font-mono-alt tabular-nums text-xs font-bold whitespace-nowrap">{issue.count}</span>
      )}
    </div>
  );
}
